import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ProgressBar, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatUSD, formatInt, formatPct } from '../shared/format';

const ACCENT = {
  rankup: TCN.cyan,
  activeness: TCN.green,
  revenue: TCN.diamond,
  recruitment: TCN.magenta,
};

const ICONS = {
  rankup: 'tiers',
  activeness: 'flame',
  revenue: 'cash',
  recruitment: 'people',
};

/**
 * Module 5 — Bonus component card.
 *
 * One per bonus type (rankup / activeness / revenue / recruitment). Shows
 * earned vs potential in USD, the matching progress bar, and 1–3 secondary
 * counters specific to each bonus. Card body fixed height ~190px so cards
 * stack cleanly in a 2x2 grid on desktop.
 *
 * Props:
 *  - kind: 'rankup' | 'activeness' | 'revenue' | 'recruitment'
 *  - earnedUsd, potentialUsd: numbers (already in USD)
 *  - secondary: array of {label, value} pairs to show below the bar
 *  - highlight: boolean — accents the card border (used for biggest-gap)
 */
const BonusCard = ({
  kind,
  earnedUsd,
  potentialUsd,
  secondary = [],
  highlight = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const accent = ACCENT[kind] ?? TCN.cyan;
  const icon = ICONS[kind] ?? 'cash';

  const earned = Number(earnedUsd ?? 0);
  const potential = Number(potentialUsd ?? 0);
  const pct = potential > 0 ? (earned / potential) * 100 : null;
  // Mockup A5.1 "Bonus mix" / A5.2-A5.5 use the bonus's semantic accent color
  // for the bar (Rank-Up cyan, Activeness green, Revenue diamond, Recruitment
  // magenta) — not a ratio-based traffic-light color.
  const barColor = accent;

  return (
    <Card
      padded
      onClick={onClick}
      style={{
        padding: 14,
        border: highlight ? `1px solid ${TCN.amber}66` : `1px solid ${TCN.border}`,
        boxShadow: highlight ? `0 0 24px ${TCN.amber}22` : 'none',
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${accent}22`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name={icon} size={16} />
        </div>
        <div
          style={{
            fontSize: 11, fontWeight: 700,
            color: TCN.textDim, letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {t(`tcn.m5.bonus.${kind}.label`, kind)}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: TCN.text, letterSpacing: '-0.5px', fontVariantNumeric: 'tabular-nums' }}>
          {formatUSD(earned, { fractionDigits: earned < 100 ? 2 : 0 })}
        </span>
        <span style={{ fontSize: 13, color: TCN.textDim, fontVariantNumeric: 'tabular-nums' }}>
          / {formatUSD(potential, { fractionDigits: potential < 100 ? 2 : 0 })}
        </span>
      </div>

      <ProgressBar value={Math.min(100, pct ?? 0)} color={barColor} />
      <div style={{ marginTop: 6, fontSize: 10, color: TCN.textMute }}>
        {pct != null ? `${formatPct(pct)} ${t('tcn.m5.bonus.earned', 'earned')}` : t('tcn.m5.bonus.noPotential', 'No potential set')}
      </div>

      {secondary.length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${TCN.border}`,
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(secondary.length, 3)}, 1fr)`,
            gap: 8,
          }}
        >
          {secondary.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: 9, color: TCN.textMute, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: TCN.text, marginTop: 2 }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default BonusCard;
